/**
 * Drill Setup Screen
 * Configure drill before starting (template, location, proctor)
 */
import { useQuery } from '@tanstack/react-query'
import { Play, Loader2, AlertCircle, WifiOff } from 'lucide-react'
import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router'

import { Button } from '@/components/elements/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/elements/card'
import { Input } from '@/components/elements/input'
import { RadioGroup, RadioGroupItem } from '@/components/elements/radio-group'
import { Layout } from '@/components/Layout'
import { useOnlineStatus } from '@/hooks/useOffline'
import { drillTimerApi } from '@/services/drillTimerService'
import { getEntityRoles } from '@/services/entity.service'
import { formatUserName } from '@/utils/userHelpers'

export function DrillSetupScreen() {
  const navigate = useNavigate()
  const { entityId } = useParams()
  const isOnline = useOnlineStatus()

  const [formData, setFormData] = useState({
    drillTimerId: '',
    locationOfDrill: '',
    proctorId: '',
  })

  const [errors, setErrors] = useState({})

  // Fetch drill templates for this entity
  const {
    data: templatesData,
    isLoading: loadingTemplates,
    error: templatesError,
  } = useQuery({
    queryKey: ['drillTemplates', entityId],
    queryFn: () => drillTimerApi.getTemplates(entityId),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    enabled: !!entityId,
  })

  const templates = templatesData?.results || templatesData || []

  // Fetch entity roles for proctor selection (admins, owners, instructors only)
  const {
    data: entityRolesData,
    isLoading: loadingUsers,
    error: usersError,
  } = useQuery({
    queryKey: ['entityRoles', entityId, 'proctors'],
    queryFn: () =>
      getEntityRoles({
        entityId,
        sorting: [],
        pagination: { pageIndex: 0, pageSize: 1000 },
        searchTerm: '',
        filters: { role: ['Owner', 'Admin', 'Instructor'] },
      }),
    staleTime: 5 * 60 * 1000,
    enabled: !!entityId,
  })

  // Extract user objects from entity roles
  const users = (entityRolesData?.results || [])
    .map((entityRole) => entityRole.user)
    .filter(Boolean)

  const validateForm = () => {
    const newErrors = {}

    if (!formData.drillTimerId) {
      newErrors.drillTimerId = 'Please select a drill template'
    }

    if (!formData.locationOfDrill.trim()) {
      newErrors.locationOfDrill = 'Please enter a location'
    }

    if (!formData.proctorId) {
      newErrors.proctorId = 'Please select a proctor'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    const selectedTemplate = templates.find((t) => t.id === formData.drillTimerId)
    const selectedProctor = users.find((u) => u.id === formData.proctorId)

    // Navigate to timer screen with drill configuration
    navigate('../active', {
      state: {
        drillTimer: selectedTemplate,
        locationOfDrill: formData.locationOfDrill,
        proctor: selectedProctor,
      },
    })
  }

  const isLoading = loadingTemplates || loadingUsers
  const hasError = templatesError || usersError

  return (
    <Layout
      breadcrumbs={[
        { pathname: `/accounts/${entityId}/drill-timer`, title: 'Drill Timer' },
        'Start Drill',
      ]}
    >
      <Layout.Heading title="Start New Drill" />

      {/* Offline Indicator */}
      {!isOnline && (
        <Card className="mb-4 border-orange-200 bg-orange-50">
          <CardContent className="py-3">
            <div className="flex items-center gap-3">
              <WifiOff className="h-5 w-5 flex-shrink-0 text-orange-600" />
              <div>
                <p className="font-medium text-orange-900">Offline Mode</p>
                <p className="text-sm text-orange-700">
                  You can complete drills offline. Make sure to save the report when done - it will
                  sync when connection is restored.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading && (
        <Card>
          <CardContent className="py-12 text-center">
            <Loader2 className="text-muted-foreground mx-auto h-8 w-8 animate-spin" />
            <p className="text-muted-foreground mt-4 text-sm">Loading drill configuration...</p>
          </CardContent>
        </Card>
      )}
      {hasError && (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="text-destructive mx-auto h-8 w-8" />
            <p className="text-destructive mt-4 text-sm">
              Failed to load drill configuration. Please try again.
            </p>
            <Button variant="outline" className="mt-4" onClick={() => window.location.reload()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      )}
      {!isLoading && !hasError && (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Drill Template Selection */}
          <Card>
            <CardHeader>
              <CardTitle>
                Drill Template <span className="text-destructive">*</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {templates.length === 0 ? (
                <div className="text-muted-foreground py-4 text-center">
                  No drill templates available. Please create a template first.
                </div>
              ) : (
                <RadioGroup
                  value={formData.drillTimerId}
                  onValueChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      drillTimerId: value,
                    }))
                  }
                  className="grid grid-cols-1 gap-3"
                >
                  {templates.map((template) => (
                    <label
                      key={template.id}
                      htmlFor={`template-${template.id}`}
                      className={`flex cursor-pointer items-start gap-3 rounded-lg border-2 p-4 transition-all ${
                        formData.drillTimerId === template.id
                          ? 'border-primary bg-primary/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <RadioGroupItem
                        id={`template-${template.id}`}
                        value={template.id}
                        className="mt-1"
                        aria-label={`Select ${template.name}`}
                      />
                      <div className="flex-1">
                        <div className="font-medium">{template.name}</div>
                        <div className="mt-1 flex items-center gap-2">
                          {template.is_rescue_ready && (
                            <span className="rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-700">
                              Rescue Ready
                            </span>
                          )}
                          <div className="text-muted-foreground flex gap-2 text-xs">
                            {template.is_sequential && <span>Sequential</span>}
                            {template.is_non_sequential && <span>Non-Sequential</span>}
                          </div>
                        </div>
                      </div>
                    </label>
                  ))}
                </RadioGroup>
              )}
              {errors.drillTimerId && (
                <p className="text-destructive text-sm">{errors.drillTimerId}</p>
              )}
            </CardContent>
          </Card>

          {/* Location Input */}
          <Card>
            <CardHeader>
              <CardTitle>
                Location of Drill <span className="text-destructive">*</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Input
                type="text"
                placeholder="e.g., Main Pool, Deep End"
                value={formData.locationOfDrill}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    locationOfDrill: e.target.value,
                  }))
                }
              />
              {errors.locationOfDrill && (
                <p className="text-destructive mt-2 text-sm">{errors.locationOfDrill}</p>
              )}
            </CardContent>
          </Card>

          {/* Proctor Selection */}
          <Card>
            <CardHeader>
              <CardTitle>
                Proctor <span className="text-destructive">*</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {users.length === 0 ? (
                <div className="text-muted-foreground py-4 text-center">No users available</div>
              ) : (
                <RadioGroup
                  value={formData.proctorId}
                  onValueChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      proctorId: value,
                    }))
                  }
                  className="grid grid-cols-1 gap-2"
                >
                  {users.map((user) => (
                    <label
                      key={user.id}
                      htmlFor={`proctor-${user.id}`}
                      className={`flex cursor-pointer items-center gap-3 rounded-lg border-2 p-3 transition-all ${
                        formData.proctorId === user.id.toString()
                          ? 'border-primary bg-primary/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <RadioGroupItem
                        id={`proctor-${user.id}`}
                        value={user.id.toString()}
                        aria-label={`Select ${formatUserName(user)}`}
                      />
                      <div className="flex-1">
                        <div className="font-medium">{formatUserName(user)}</div>
                        {user.email && (
                          <div className="text-muted-foreground text-sm">{user.email}</div>
                        )}
                      </div>
                    </label>
                  ))}
                </RadioGroup>
              )}
              {errors.proctorId && <p className="text-destructive text-sm">{errors.proctorId}</p>}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('..')}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || templates.length === 0}
              className="flex-1"
            >
              <Play className="mr-2 h-4 w-4" />
              Start Drill
            </Button>
          </div>
        </form>
      )}
    </Layout>
  )
}
